import { useCallback } from 'react'

import useAppState from '../../hooks/useAppState'
import { updateUser } from '../../apiLayer'
import { getCommunicationAudiencesForRoles } from '../../../../pure-js/libs/communicationCategories'
import { OrgRole } from '../../../../pure-js/types/OrganizationMembership'
import type { CommunicationPreferences } from '../../../../pure-js/types/Preferences'
import type { User } from '../../../../pure-js/types/User'

export interface UseCommunicationPreferencesResult {
  preferences: CommunicationPreferences | undefined
  audiences: OrgRole[] // union of the user's org roles; gamer fallback when no memberships
  save: (next: CommunicationPreferences) => Promise<void>
}

// Reads prefs from AppState and saves via the shared updateUser flow, mirroring the result locally.
export const useCommunicationPreferences = (): UseCommunicationPreferencesResult => {
  const { state, setState } = useAppState()
  const preferences = state.user?.preferences?.communication

  const roles = (state.user?.organizations ?? []).map((membership) => membership.role)
  const derived = getCommunicationAudiencesForRoles(roles)
  const audiences: OrgRole[] = derived.length > 0 ? derived : [OrgRole.GAMER]

  const save = useCallback(
    async (next: CommunicationPreferences) => {
      const userId = state.user?.id
      if (!userId) throw new Error('No user ID found')

      const value: CommunicationPreferences = { ...next, updatedAt: new Date().toISOString() }
      await updateUser(userId, { preferences: { communication: value } } as Partial<User>)

      setState({
        ...state,
        user: { ...state.user, preferences: { ...state.user?.preferences, communication: value } }
      })
    },
    [state, setState]
  )

  return { preferences, audiences, save }
}
